<template>
  <div class="correlation-content">
    <input-text class="title-input" :label="$t('correlationsPage.correlationContent.titleLabel')" :maxlength="100" v-model="name" @input="change" />
    <correlation-diagram
      :novelId="novelId"
      :diagram="diagram"
      :characterItems="characterItems"
      :selectedCorrelationList="filterCorrelationList()"
      @clickedShowSelectCorrelation="isSelecting = !isSelecting"
      @move="moveCharacter"
    />
    <div v-if="isSelecting" class="select-correlation">
      <div class="content">
        <div class="head" @click="isSelecting = false">{{ $t('correlationsPage.correlationContent.closeWindow') }}</div>
        <div class="description">{{ $t('correlationsPage.correlationContent.selectDescription') }}</div>
        <div v-for="item in correlationList" :key="item.correlationId" class="list-item">
          <correlation-list-item
            previewMode
            :key="item.correlationId"
            :characterA="getCharacterData(item.characterA)"
            :characterB="getCharacterData(item.characterB)"
            :relationshipAtoB="item.relationshipAtoB"
            :relationshipBtoA="item.relationshipBtoA"
          />
          <div class="select-button">
            <button
              :class="{ button: true, selected: isSelectedCorrelation(item.correlationId) }"
              @click="selectCorrelation(item)"
            >
              {{ isSelectedCorrelation(item.correlationId) ? $t('correlationsPage.correlationContent.selectedButton') : $t('correlationsPage.correlationContent.unselectedButton') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="bottom">
      <button :disabled="!changed" class="button submit" @click="save">{{ $t('common.save') }}</button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { callApi } from "@/lib/api";
import InputText from "@/components/atoms/InputText.vue";
import CorrelationDiagram from "@/components/organisms/CorrelationDiagram.vue";
import CorrelationListItem from "@/components/molecules/CorrelationListItem.vue";
import { getCharacterKey } from "@/lib/s3";
import { showNotifyDialog } from "@/lib/dialog";
import equal from "deep-equal";
import clone from "lodash/cloneDeep";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";

export default Vue.extend({
  components: {
    InputText,
    CorrelationDiagram,
    CorrelationListItem,
  },
  props: {
    novelId: String,
    correlationGroupName: String,
    selectedCorrelationGroupId: String,
    items: Array as any,
    characterItems: Array as any,
    correlationDiagram: Object,
  },
  data() {
    return {
      name: this.correlationGroupName,
      changed: false,
      isSelecting: false,
      correlationList: this.items,
      diagram: this.correlationDiagram || {
        correlationKeys: [],
        coordinate: [],
      },
      unbindShortcutKeys: null,
    };
  },
  methods: {
    async save() {
      const { novelId, name, diagram } = this;

      const query = `
      mutation UpdateCorrelationGroup ($input: CorrelationGroupUpdateInput!) {
        updateCorrelationGroup (input: $input) {
          novelId
          correlationGroupId
          name
          diagram {
            correlationKeys
            coordinate {
              characterId
              x
              y
            }
          }
        }
      }
      `;
      await callApi(query, {
        input: {
          novelId,
          correlationGroupId: this.selectedCorrelationGroupId,
          name,
          diagram,
        },
      });

      this.setModified((this.changed = false));
      this.$emit("fetch");

      this.$notify({
        title: this.$t('common.saved'),
        type: "success",
      });

      // 保存ボタンのショートカットキーを解除
      if (this.unbindShortcutKeys) {
        this.unbindShortcutKeys();
        this.unbindShortcutKeys = null; // 再バインドを防ぐためにリセット
      }
    },
    change(e) {
      if (this.correlationGroupName === this.name && equal(this.correlationDiagram, this.diagram)) {
        this.setModified((this.changed = false));
      } else {
        this.setModified((this.changed = true));

        // すでにバインドされている場合は再バインドしない
        if (!this.unbindShortcutKeys) {
          // 保存ボタンのショートカットキーの有効化と解除関数の取得
          this.unbindShortcutKeys = handleShortcutKeys([
            { trigger: TriggerKey.Meta, keys: ["s"], callback: this.save },
            { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.save },
          ]);
        }
      }
    },
    getCharacterData(characterId) {
      const character = this.characterItems.find((item) => item.characterId === characterId);
      character.image = getCharacterKey(this.novelId, character);
      return character;
    },
    isSelectedCorrelation(correlationId) {
      return this.diagram.correlationKeys.includes(correlationId);
    },
    selectCorrelation(correlation) {
      const { correlationId, characterA, characterB } = correlation;

      if (this.isSelectedCorrelation(correlationId)) {
        this.diagram.correlationKeys = this.diagram.correlationKeys.filter((key) => key !== correlationId);
        this.removeCoordinate(characterA, characterB);
      } else {
        this.diagram.correlationKeys.push(correlationId);
        this.setInitialCoordinate(characterA, characterB);
      }
      this.change();
    },
    removeCoordinate(characterA, characterB) {
      let isRemoveCharacterA = true;
      let isRemoveCharacterB = true;

      // 相関図に表示されているすべての相関関係を取得（自身の相関関係はselectCorrelation()でcorrelationKeysから除外済み）
      const selectedCorrelationList = this.correlationList.filter((item) =>
        this.diagram.correlationKeys.find((key) => key === item.correlationId)
      );

      // 相関関係にcharacterA or characterBが含まれている場合は除外対象から外す
      selectedCorrelationList.forEach((item) => {
        if (characterA === item.characterA || characterA === item.characterB) {
          isRemoveCharacterA = false;
        }
        if (characterB === item.characterA || characterB === item.characterB) {
          isRemoveCharacterB = false;
        }
      });

      // coordinateから除外
      if (isRemoveCharacterA) {
        this.diagram.coordinate = this.diagram.coordinate.filter((item) => item.characterId !== characterA);
      }
      if (isRemoveCharacterB) {
        this.diagram.coordinate = this.diagram.coordinate.filter((item) => item.characterId !== characterB);
      }
    },
    async setInitialCoordinate(characterA, characterB) {
      const hasCharacterA = this.diagram.coordinate.some((item) => item.characterId === characterA);
      const hasCharacterB = this.diagram.coordinate.some((item) => item.characterId === characterB);

      // 指定のcharacterがcoordinateに存在しない場合は追加
      if (!hasCharacterA) {
        const { x, y } = await this.calcCoordinate();
        this.diagram.coordinate.push({
          characterId: characterA,
          x,
          y,
        });
      }
      if (!hasCharacterB) {
        const { x, y } = await this.calcCoordinate();
        this.diagram.coordinate.push({
          characterId: characterB,
          x,
          y,
        });
      }
    },
    /** 配置可能な座標を計算 */
    async calcCoordinate() {
      let x = 0;
      let y = 0;
      let i = 0;
      let isBreak = false;

      // 右下方向(x++, y++)
      while (i <= 40) {
        if (this.checkCoordinate(x, y)) {
          isBreak = true;
          break;
        }
        const isMaxX = x >= i;
        const isMaxY = y >= i;
        if (isMaxX && isMaxY) {
          y = 0;
          i += 1;
          x = i;
        } else if (isMaxX) {
          y += 1;
          if (y >= i) {
            x = 0;
          }
        } else {
          x += 1;
        }
      }
      if (isBreak) {
        return { x, y };
      }

      // 左上方向(x--, y--)
      x = 0;
      y = 0;
      i = 0;
      while (i <= -40) {
        if (this.checkCoordinate(x, y)) {
          isBreak = true;
          break;
        }
        const isMaxX = x <= i;
        const isMaxY = y <= i;
        if (isMaxX && isMaxY) {
          y = 0;
          i -= 1;
          x = i;
        } else if (isMaxX) {
          y -= 1;
          if (y <= i) {
            x = 0;
          }
        } else {
          x -= 1;
        }
      }
      if (isBreak) {
        return { x, y };
      }

      // 右上方向(x++, y--)
      x = 0;
      y = 0;
      i = 0;
      while (Math.abs(i) >= 40) {
        if (this.checkCoordinate(x, y)) {
          isBreak = true;
          break;
        }
        const isMaxX = x >= i;
        const isMaxY = y <= -i;
        if (isMaxX && isMaxY) {
          y = 0;
          i += 1;
          x = i;
        } else if (isMaxX) {
          y -= 1;
          if (y <= -i) {
            x = 0;
          }
        } else {
          x += 1;
        }
      }
      if (isBreak) {
        return { x, y };
      }

      // 左下方向(x--, y++)
      x = 0;
      y = 0;
      i = 0;
      while (Math.abs(i) >= 40) {
        if (this.checkCoordinate(x, y)) {
          isBreak = true;
          break;
        }
        const isMaxX = x <= -i;
        const isMaxY = y >= i;
        if (isMaxX && isMaxY) {
          y = 0;
          i += 1;
          x = -i;
        } else if (isMaxX) {
          y += 1;
          if (y >= i) {
            x = 0;
          }
        } else {
          x -= 1;
        }
      }
      if (isBreak) {
        return { x, y };
      }

      // alert
      await showNotifyDialog({
        title: this.$t('correlationsPage.correlationContent.diagramError') as string,
        content: this.$t('correlationsPage.correlationContent.diagramLimitReached') as string,
      });

      return { x: 0, y: 0 };
    },
    /** 配置可能な座標かチェック */
    checkCoordinate(x, y, characterId = null) {
      // 有効な座標範囲: (-40, -40) <= (x, y) <= (40, 40)
      if (Math.abs(x) > 40 || Math.abs(y) > 40) {
        return false;
      }

      let { coordinate } = this.diagram;

      if (characterId) {
        coordinate = coordinate.filter((item) => item.characterId !== characterId);
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const item of coordinate) {
        // 2点間の距離 = √((x2 - x1) ^ 2 + (y2 - y1) ^ 2)
        const distance = Math.sqrt((x - item.x) ** 2 + (y - item.y) ** 2);
        if (distance < 3) {
          return false;
        }
      }
      return true;
    },
    moveCharacter(payload) {
      const { characterId, x, y } = payload;

      if (this.checkCoordinate(x, y, characterId)) {
        this.diagram.coordinate = this.diagram.coordinate.filter((item) => item.characterId !== characterId);
        this.diagram.coordinate.push({
          characterId,
          x,
          y,
        });
      }

      this.change();
    },
    filterCorrelationList() {
      if (!this.diagram) {
        this.diagram = {
          correlationKeys: [],
          coordinate: [],
        };
      }

      const { correlationList } = this;
      const { correlationKeys } = this.diagram;

      return correlationList.filter((item) => correlationKeys.find((key) => key === item.correlationId));
    },
  },
  watch: {
    correlationGroupName() {
      this.name = this.correlationGroupName;
    },
    items() {
      this.correlationList = clone(this.items);
    },
    correlationDiagram: {
      handler() {
        this.diagram = clone(this.correlationDiagram);
      },
      deep: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.correlation-content {
  display: grid;
  height: 100vh;
  width: 100%;
  grid-template-columns: 1fr;
  grid-template-rows: 55px 1fr;
  border-left: 1px solid var(--color-border);
  position: relative;
}

/** タイトルのレイアウト */
.correlation-content ::v-deep .title-input.input {
  box-sizing: border-box;
  font-size: var(--font-size-md);
  border-radius: 0;
  border-bottom: 4px double var(--color-border);
  border-top: none;
  border-left: none;
  border-right: none;
  padding: 15px 30px;
  margin: 0;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.button {
  padding: 10px 30px;
  border-radius: var(--radius-xs);
  margin-right: auto;
  margin-left: 30px;
  background-color: transparent;
  color: var(--color-text-normal);
  border: 1px solid var(--color-border);
}

/*PC：フッターのレイアウト*/
.bottom {
  display: inline-block;
  flex-grow: 0.1;
  background-color: var(--color-surface-subtle);
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .submit {
    position: relative;

    &:not(:disabled) {
      color: var(--color-text-on-fixed-bg);
      background-color: var(--color-primary);
      border-color: var(--color-primary);

      @include hover-overlay;
    }

    &:disabled {
      background-color: var(--color-surface-muted);
      color: var(--color-inactive);
      border: solid 1px var(--color-border);
    }
  }
}

.select-correlation {
  position: absolute;
  width: 100%;
  height: 100%;
  background: var(--color-overlay);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;

  .content {
    overflow: auto;
    width: 400px;
    height: calc(100vh * 0.9);
    background: var(--color-background-normal);

    .head {
      position: sticky;
      top: 0;
      background-color: var(--color-surface-subtle);
      color: var(--color-text-normal);
      box-sizing: border-box;
      width: 100%;
      padding: 10px 15px;
      cursor: pointer;
    }

    .description {
      background: var(--color-surface-subtle);
      color: var(--color-text-normal);
      font-weight: var(--font-weight-bold);
      padding: 10px 15px;
    }

    .list-item {
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid var(--color-border);

      li {
        border: none;
      }

      .select-button {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-grow: 1;

        .button {
          margin: 0;
          padding: 10px;
          color: var(--color-text-normal);
          background-color: var(--color-background-normal);
          border-color: var(--color-border);

          &.selected {
            color: var(--color-primary);
            border-color: var(--color-primary);
          }
        }
      }
    }
  }
}
</style>
